import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { createPortal } from "react-dom";

export type SearchableSelectOption = {
  value: string;
  label: string;
  /** Quiet group header; options with the same group render together. */
  group?: string;
  /** Extra searchable text (tags, aliases) — not shown as the primary label. */
  hint?: string;
};

export type SearchableSelectFilter = {
  id: string;
  label: string;
};

export type SearchableSelectProps = {
  value: string;
  options: readonly SearchableSelectOption[];
  onChange: (value: string) => void;
  ariaLabel?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  placeholder?: string;
  disabled?: boolean;
  filters?: readonly SearchableSelectFilter[];
  className?: string;
  /** Fired when the floating panel opens (e.g. refresh catalog). */
  onOpen?: () => void;
};

/**
 * Curva-style searchable floating select (React port of SearchableFloatingSelect).
 * Panel portals to document.body.
 */
export function SearchableSelect({
  value,
  options,
  onChange,
  ariaLabel,
  searchPlaceholder = "Search",
  emptyMessage = "No matches.",
  placeholder = "Select…",
  disabled = false,
  filters = [],
  className = "",
  onOpen,
}: SearchableSelectProps) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const rowRefs = useRef(new Map<string, HTMLButtonElement>());

  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [filterId, setFilterId] = useState("all");
  const [highlighted, setHighlighted] = useState(value);
  const [position, setPosition] = useState<{ left: number; top: number; minWidth: number } | undefined>();

  const selected = options.find(o => o.value === value);
  const triggerLabel = selected?.label ?? placeholder;

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return options.filter(option => {
      if (filterId !== "all" && option.group !== filterId) {
        return false;
      }
      if (!needle) {
        return true;
      }
      const haystack = `${option.label} ${option.group ?? ""} ${option.hint ?? ""}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [options, query, filterId]);

  useEffect(() => {
    if (!open) {
      return;
    }
    if (!filtered.some(o => o.value === highlighted)) {
      setHighlighted(filtered[0]?.value ?? value);
    }
  }, [open, filtered, highlighted, value]);

  useLayoutEffect(() => {
    if (!open) {
      setPosition(undefined);
      return;
    }

    function updatePosition(): void {
      const trigger = triggerRef.current;
      const panel = panelRef.current;
      if (!trigger || !panel) {
        return;
      }
      const rect = trigger.getBoundingClientRect();
      const margin = 8;
      const gap = 4;
      panel.style.minWidth = `${Math.max(rect.width, 260)}px`;
      const width = panel.offsetWidth;
      const height = panel.offsetHeight;
      let left = rect.right - width;
      left = Math.min(Math.max(margin, left), Math.max(margin, window.innerWidth - width - margin));
      const belowTop = rect.bottom + gap;
      const aboveTop = rect.top - height - gap;
      const fitsBelow = belowTop + height <= window.innerHeight - margin;
      const top = fitsBelow ? belowTop : Math.max(margin, aboveTop);
      setPosition({ left, top, minWidth: Math.max(rect.width, 260) });
    }

    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
    };
  }, [open, filtered, filterId, query]);

  useEffect(() => {
    if (!open) {
      return;
    }
    requestAnimationFrame(() => {
      searchRef.current?.focus();
      searchRef.current?.select();
      scrollRowIntoView(highlighted);
    });
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onPointerDown(event: PointerEvent): void {
      const target = event.target;
      if (!(target instanceof Node)) {
        return;
      }
      if (triggerRef.current?.contains(target) || panelRef.current?.contains(target)) {
        return;
      }
      setOpen(false);
    }
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === "Escape") {
        event.preventDefault();
        setOpen(false);
        triggerRef.current?.focus();
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        moveHighlight(1);
        return;
      }
      if (event.key === "ArrowUp") {
        event.preventDefault();
        moveHighlight(-1);
        return;
      }
      if (event.key === "Enter") {
        event.preventDefault();
        if (highlighted) {
          commit(highlighted);
        }
      }
    }
    function onWheel(event: WheelEvent): void {
      const target = event.target;
      if (target instanceof Node && (triggerRef.current?.contains(target) || panelRef.current?.contains(target))) {
        return;
      }
      setOpen(false);
    }
    window.addEventListener("pointerdown", onPointerDown, true);
    window.addEventListener("keydown", onKeyDown);
    window.addEventListener("wheel", onWheel, { capture: true, passive: true });
    return () => {
      window.removeEventListener("pointerdown", onPointerDown, true);
      window.removeEventListener("keydown", onKeyDown);
      window.removeEventListener("wheel", onWheel, true);
    };
  }, [open, highlighted, filtered]);

  function scrollRowIntoView(optionValue: string): void {
    const list = listRef.current;
    const row = rowRefs.current.get(optionValue);
    if (!list || !row) {
      return;
    }
    const rowTop = row.offsetTop;
    const rowBottom = rowTop + row.offsetHeight;
    if (rowTop < list.scrollTop) {
      list.scrollTop = rowTop;
    } else if (rowBottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = rowBottom - list.clientHeight;
    }
  }

  function moveHighlight(delta: number): void {
    if (filtered.length === 0) {
      return;
    }
    const currentIndex = Math.max(0, filtered.findIndex(o => o.value === highlighted));
    const nextIndex = Math.min(filtered.length - 1, Math.max(0, currentIndex + delta));
    const next = filtered[nextIndex];
    if (!next) {
      return;
    }
    setHighlighted(next.value);
    requestAnimationFrame(() => scrollRowIntoView(next.value));
  }

  function commit(next: string): void {
    onChange(next);
    setOpen(false);
    triggerRef.current?.focus();
  }

  function openPanel(): void {
    if (disabled) {
      return;
    }
    setQuery("");
    setHighlighted(value);
    onOpen?.();
    setOpen(true);
  }

  const rows: ReactNode[] = [];
  let lastGroup: string | undefined;
  for (const option of filtered) {
    const group = option.group?.trim();
    if (group && group !== lastGroup) {
      lastGroup = group;
      const filter = filters.find(entry => entry.id === group);
      rows.push(
        <div
          key={`g:${group}`}
          className="px-2 pb-0.5 pt-[0.45rem] text-[10px] uppercase tracking-[0.06em] text-slate-500 first:pt-1"
        >
          {filter?.label ?? group}
        </div>,
      );
    }
    const isHighlighted = option.value === highlighted;
    const isSelected = option.value === value;
    // Exclusive tones — don't stack idle + hover + selected bg utilities.
    const rowTone = isHighlighted
      ? "bg-[rgba(61,139,253,0.2)] text-sky-100"
      : isSelected
        ? "bg-[rgba(61,139,253,0.12)] text-sky-100 hover:bg-[rgba(61,139,253,0.2)]"
        : "bg-transparent text-slate-200 hover:bg-[rgba(61,139,253,0.2)] hover:text-sky-100";
    const hintTone = isHighlighted ? "text-slate-400" : "text-slate-500 group-hover:text-slate-400";
    rows.push(
      <button
        key={option.value}
        ref={el => {
          if (el) {
            rowRefs.current.set(option.value, el);
          } else {
            rowRefs.current.delete(option.value);
          }
        }}
        type="button"
        role="option"
        className={`group flex h-9 w-full items-center gap-2 border-0 px-2 text-left text-xs [font:inherit] ${rowTone}`}
        aria-selected={isSelected}
        title={option.hint ? `${option.label} · ${option.hint}` : option.label}
        onMouseEnter={() => setHighlighted(option.value)}
        onClick={() => commit(option.value)}
      >
        <span className="grid size-4 shrink-0 place-items-center text-sky-300" aria-hidden={true}>
          {isSelected ? (
            <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          ) : null}
        </span>
        <span className="min-w-0 flex-1 truncate">{option.label}</span>
        {option.hint ? (
          <span className={`max-w-[40%] shrink truncate text-[10px] ${hintTone}`}>{option.hint}</span>
        ) : null}
      </button>,
    );
  }

  return (
    <div className={`relative min-w-0${className ? ` ${className}` : ""}`}>
      <button
        ref={triggerRef}
        type="button"
        className="flex h-8 w-full items-center justify-between gap-2 rounded-md border border-white/12 bg-white/[0.04] px-2 text-left text-xs text-slate-200 outline-none hover:bg-white/[0.06] focus-visible:border-[rgba(61,139,253,0.45)] disabled:cursor-not-allowed disabled:opacity-50"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        title={triggerLabel}
        disabled={disabled}
        onClick={() => (open ? setOpen(false) : openPanel())}
      >
        <span className="min-w-0 truncate">{triggerLabel}</span>
        <span className="grid shrink-0 place-items-center text-slate-500" aria-hidden={true}>
          <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </span>
      </button>

      {open
        ? createPortal(
          <div
            ref={panelRef}
            role="listbox"
            className="fixed z-[80] w-72 max-w-[calc(100vw-1rem)] rounded-md border border-white/10 bg-lab-panel p-3 text-xs text-slate-300 shadow-[0_18px_40px_rgba(0,0,0,0.45)]"
            data-floating-ui=""
            style={
              position
                ? { left: position.left, top: position.top, minWidth: position.minWidth }
                : { left: 0, top: 0, visibility: "hidden" }
            }
            onPointerDown={event => event.stopPropagation()}
            onWheel={event => event.stopPropagation()}
          >
            <div className="relative mb-2">
              <span
                className="pointer-events-none absolute top-1/2 left-2 grid -translate-y-1/2 place-items-center text-slate-500"
                aria-hidden={true}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="7" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
              </span>
              <input
                ref={searchRef}
                type="search"
                className="h-8 w-full rounded border border-white/10 bg-white/[0.04] py-0 pr-2 pl-7 text-xs text-slate-100 outline-none placeholder:text-slate-500 focus:border-[rgba(61,139,253,0.45)]"
                placeholder={searchPlaceholder}
                autoComplete="off"
                spellCheck={false}
                value={query}
                onChange={event => setQuery(event.target.value)}
                onKeyDown={event => {
                  if (event.key === "ArrowDown" || event.key === "ArrowUp") {
                    event.preventDefault();
                    event.stopPropagation();
                    moveHighlight(event.key === "ArrowDown" ? 1 : -1);
                  }
                }}
              />
            </div>

            {filters.length > 0 ? (
              <div className="-mt-0.5 mb-[0.55rem] flex flex-wrap gap-x-[0.55rem] gap-y-[0.15rem]">
                <FilterButton
                  id="all"
                  label="All"
                  active={filterId === "all"}
                  onSelect={setFilterId}
                />
                {filters.map(filter => (
                  <FilterButton
                    key={filter.id}
                    id={filter.id}
                    label={filter.label}
                    active={filterId === filter.id}
                    onSelect={setFilterId}
                  />
                ))}
              </div>
            ) : null}

            <div
              ref={listRef}
              className="max-h-72 overflow-y-auto rounded border border-white/10"
            >
              {rows.length === 0 ? (
                <div className="px-2 py-3 text-center text-[11px] text-slate-500">{emptyMessage}</div>
              ) : (
                rows
              )}
            </div>
          </div>,
          document.body,
        )
        : null}
    </div>
  );
}

function FilterButton({
  id,
  label,
  active,
  onSelect,
}: {
  id: string;
  label: string;
  active: boolean;
  onSelect: (id: string) => void;
}) {
  // Exclusive active vs idle — underline must not fight muted text utilities.
  const tone = active
    ? "text-slate-300 underline underline-offset-[0.18em]"
    : "text-slate-500 hover:text-slate-400";
  return (
    <button
      type="button"
      className={`border-0 bg-transparent p-0 text-[11px] tracking-[0.01em] ${tone}`}
      aria-pressed={active}
      onClick={() => onSelect(id)}
    >
      {label}
    </button>
  );
}
